import useScreenSize from "hoc/useScreenSize";
import useOutsideClick from "hooks-contexts/useOutsideClick";
import { useRouter } from "next/router";
import {
  cloneElement, createRef, MouseEvent, useEffect, useState,
} from "react";
import HideIf from "util/HideIf";
import popoverStyles from "./popover.module.scss";

function Popover(props: any) {
  const [tooltipShow, setTooltipShow] = useState(false);
  const [applyActiveClass, setApplyActiveClass] = useState(false);
  const [opp, setState] = useState(0);
  const {
    component, overlay, children, placement,
    className = "", lazyLoad = false, popoverContentClassName = "",
  } = props;

  const btnRef = createRef();
  const tooltipRef = createRef();
  const arrowRef = createRef();
  const [showPopover, setShowPopover] = useState(!lazyLoad); // show node in DOM by default
  const [, , isDesktop] = useScreenSize();
  const router = useRouter();

  useOutsideClick(tooltipRef, onOutsideClick, !applyActiveClass);

  const openTooltip = () => new Promise((resolve) => {
    import("@popperjs/core")
      .then((module) => {
        const popperInstance = module.createPopper(btnRef.current, tooltipRef.current, {
          placement: placement || "bottom",
        });
        // When popover is dynamically loaded, it's position is not updated. That needs to be updated
        setTooltipShow(true);
        setApplyActiveClass(true);
        updateLazyLoad(true);

        setTimeout(() => {
          popperInstance.update();
        }, 10);
        resolve(popperInstance);
      });
  });

  const closeTooltip = () => {
    setTooltipShow(false);
    updateLazyLoad(false);
  };

  function onOutsideClick(event: MouseEvent) {
    // When clicked on tooltip button, it is also treated as outside click, but it should not be
    if (btnRef && btnRef.current && btnRef.current.contains(event.target)) {
      return;
    }
    setApplyActiveClass(false);
    closeTooltip();
    updateLazyLoad(false);
  }

  useEffect(() => {
    router.events.on("routeChangeComplete", hidePopover);
    return () => {
      router.events.off("routeChangeComplete", hidePopover);
    };
  }, []);

  function hidePopover() {
    setTooltipShow(false);
    setState(Math.random());
  }

  function handleTooltipClick() {
    if (isDesktop) {
      return;
    }
    if (!tooltipShow) {
      openTooltip();
      updateLazyLoad(true);
    } else {
      closeTooltip();
      updateLazyLoad(false);
    }
  }

  const renderItem = cloneElement(children, {
    onMouseEnter: () => {
      if (isDesktop) {
        openTooltip();
      }
    },
    onMouseLeave: () => {
      if (isDesktop) {
        setApplyActiveClass(false);
      }
    },
    onClick: handleTooltipClick,
    ref: btnRef,
  });

  function updateLazyLoad(newShowPopover: boolean) {
    if (!lazyLoad) {
      return;
    }
    setShowPopover(newShowPopover);
  }

  function handleMouseLeave() {
    updateLazyLoad(false);
  }

  return (
    <div
      key={opp}
      className={`${popoverStyles.styleContainer} ${className}`}
    >
      <div
        className={popoverStyles.popover__wrapper}
        onMouseLeave={handleMouseLeave}
      >
        {renderItem}

        <div
          className={`${tooltipShow ? "" : popoverStyles.hidden_tooltip} ${popoverStyles.popover__content} ${applyActiveClass ? popoverStyles.active_hover : ""} ${popoverContentClassName}`}
          id="tooltip"
          ref={tooltipRef}
        >
          <HideIf show={showPopover}>
            <div
              data-popper-arrow
              ref={arrowRef}
              id="arrow"
              className={popoverStyles.arrow}
            />
            {overlay || component}
          </HideIf>
        </div>
      </div>
    </div>
  );
}

export default Popover;
